import {Fzf} from 'fzf';
import {Atom, atom, useAtomValue, useSetAtom} from 'jotai';
import React, {ComponentProps, Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {FixedSizeList} from 'react-window';
import {debounce} from '~/library/toolbox/src';
import {RsnDialog} from '..';
import RsnInput from '../RsnInput/RsnInput';
import {RsnSelect, RsnSelectOption, getKey} from './RsnSelect';
import {OptionObject, RsnSelectProps, DropdownProps as _DropdownProps} from './RsnSelect.types';

export function RsnSelectSearch<T>({strategy, virtualized, ...props}: Props<T>) {
    return (
        <RsnSelect
            {...props}
            renderDropdown={renderProps => (
                <RsnSelectSearchDropdown virtualized={virtualized} strategy={strategy} {...renderProps} />
            )}
        />
    );
}

function RsnSelectSearchDropdown<T>({
    options,
    header,
    strategy = 'fuzzy',
    virtualized,
}: DropdownProps<T> & RsnSelectSearchOwnProps) {
    const search$ = useMemo(() => atom(''), []);
    const setSearch = useSetAtom(search$);
    const SearchStrategy = strategies[strategy];
    const ListRenderer = virtualized ? renderers.virtualized : renderers.basic;

    return (
        <RsnDialog.Window placement='fullscreen'>
            <RsnDialog.LeftHeader after={<RsnInput.Search onChange={ev => setSearch(ev.target.value)} />}>
                {header}
            </RsnDialog.LeftHeader>
            <SearchStrategy search$={search$} options={options}>
                {items => (
                    <ListRenderer list={items} empty={<NothingFound />}>
                        {(item, positions) => (
                            <RsnSelectOption option={item}>
                                <SearchHighlight text={item.label} at={positions} />
                            </RsnSelectOption>
                        )}
                    </ListRenderer>
                )}
            </SearchStrategy>
        </RsnDialog.Window>
    );
}

interface RsnSelectSearchOwnProps {
    dropdown?: never;
    strategy?: keyof typeof strategies;
    virtualized?: boolean;
}

type Props<T> = Merged<RsnSelectProps<T>, RsnSelectSearchOwnProps>;
type DropdownProps<T> = Merged<_DropdownProps<T>, RsnSelectSearchOwnProps>;
type Merged<T, S> = Omit<T, keyof S> & S;

const strategies = {
    fuzzy: FuzzySearchStrategy,
    basic: BasicSearchStrategy,
};

const renderers = {
    virtualized: VirtualizedListRenderer,
    basic: BasicListRenderer,
};

function NothingFound() {
    return <div className='fv-text-xl p-4 text-center text-neutral-60'>Nothing was found</div>;
}

interface PositionsMatcher {
    has: (n: number) => boolean;
}

type SearchResultList<T> = Array<{item: OptionObject<T>; positions: PositionsMatcher}>;

interface SearchStrategyProps<T> {
    search$: Atom<string>;
    options: Array<OptionObject<T>>;
    children: (list: SearchResultList<T>) => React.JSX.Element;
}

interface ListRendererProps<T> {
    empty: React.JSX.Element;
    list: SearchResultList<T>;
    children: (option: OptionObject<T>, positions: PositionsMatcher) => React.JSX.Element;
}

function BasicSearchStrategy<T>({children, options, search$}: SearchStrategyProps<T>) {
    const search = useAtomValue(search$);
    const filterArgs = [options, search] as const;
    const found = useMemo(() => [...basicOptionsFilter(...filterArgs)], filterArgs);
    return children(found);
}
function* basicOptionsFilter<T>(options: OptionObject<T>[], search: string) {
    const diacritics = /\p{Diacritic}/gu;
    for (let i = 0; i < options.length; i++) {
        let query = search.normalize('NFC').toLocaleLowerCase();
        let label = options[i].label.normalize('NFC').toLocaleLowerCase();
        if (!diacritics.test(query)) {
            // unless there's diacritics in search string we want to allow searching with just latin script
            query = query.replace(diacritics, '');
            label = label.replace(diacritics, '');
        }
        const matchIndex = label.indexOf(search.toLowerCase());
        if (matchIndex === -1) {
            continue;
        }
        const positions: PositionsMatcher = {
            has: n => matchIndex <= n && n < query.length + matchIndex,
        };
        const item = options[i];
        yield {item, positions};
    }
}

function FuzzySearchStrategy<T>({children, options, search$}: SearchStrategyProps<T>) {
    const indexed = useMemo(() => new Fzf(options, {selector: op => op.label, casing: 'case-insensitive'}), [options]);
    const search = useAtomValue(search$);
    const found = useMemo(() => indexed.find(search), [search, indexed]);
    return children(found);
}

// TODO: benchmark with hard coded values and with HeightsRuler
// const MAGIC_OPTION_ITEM_HEIGHT = 3.375; // 54px in REM; 38 option height + 16 gap height
// const MAGIC_AVAILABLE_LIST_HEIGHT = 10.75; // 172px in REM; 16 * 2 backdrop padding + 141 header with search input height

function VirtualizedListRenderer<T>({children, list, empty}: ListRendererProps<T>) {
    const [measurements, setMeasurements] = useState<Measurements | null>(null);

    const windowHeight = useRef(0);
    const listRef = useRef<HTMLElement>(null);

    useEffect(() => {
        windowHeight.current = window.innerHeight;
        const onResize = debounce(() => {
            if (windowHeight.current !== window.innerHeight) {
                windowHeight.current = window.innerHeight;
                setMeasurements(current => current && {...current, fresh: false});
            }
        }, 100);
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, []);

    const item = list.at(0);
    if (!item) {
        return empty;
    }
    return (
        <Fragment>
            <FixedSizeList
                itemCount={list.length}
                itemSize={measurements?.itemHeight || 0}
                height={measurements?.listHeight || 0}
                width='100%'
                outerRef={listRef}
                outerElementType={ScrollBoundaryDiv}
                innerElementType={ScrolledContentUl}
                itemKey={i => getKey(list[i].item)}
            >
                {({index, style}) => <li style={style}>{children(list[index].item, list[index].positions)}</li>}
            </FixedSizeList>
            {!measurements?.fresh && (
                <HeightsRuler
                    list={listRef}
                    item={children(item.item, new Set())}
                    onGotMeasurements={setMeasurements}
                />
            )}
        </Fragment>
    );
}

interface Measurements {
    itemHeight: number;
    listHeight: number;
    fresh: boolean;
}

function HeightsRuler({
    item,
    list,
    onGotMeasurements,
}: {
    item: React.JSX.Element;
    list: React.RefObject<HTMLElement>;
    onGotMeasurements: (ms: Measurements) => void;
}) {
    const ref = {
        self: useRef<HTMLDivElement>(null),
        item: useRef<HTMLDivElement>(null),
    };
    useLayoutEffect(() => {
        const [self, item] = [ref.self.current, ref.item.current];
        if (!self || !item) return;

        const listParent = self.parentElement!;
        const parentHeight = parseFloat(getComputedStyle(listParent).height);

        const siblingsHeight = [...listParent.children]
            .filter(child => child !== self && child !== list.current)
            .map(child => parseFloat(getComputedStyle(child).height))
            .reduce((sum, next) => sum + next);

        onGotMeasurements({
            listHeight: parentHeight - siblingsHeight,
            itemHeight: parseFloat(getComputedStyle(item).height),
            fresh: true,
        });
    }, []);
    return (
        <div ref={ref.self} style={{height: window.innerHeight}}>
            <div style={{opacity: 0}} ref={ref.item}>
                {item}
            </div>
        </div>
    );
}

function BasicListRenderer<T>({children, list, empty}: ListRendererProps<T>) {
    if (list.length === 0) {
        return empty;
    }
    return (
        <ScrollBoundaryDiv>
            <ScrolledContentUl>
                {list.map(({item, positions}) => (
                    <li key={getKey(item)}>{children(item, positions)}</li>
                ))}
            </ScrolledContentUl>
        </ScrollBoundaryDiv>
    );
}

const ScrollBoundaryDiv = React.forwardRef(function ScrollBoundary(
    {children, ...props}: ComponentProps<'div'>,
    ref: React.Ref<HTMLDivElement>
) {
    return (
        <div {...props} ref={ref} className={RsnDialog.classes.body({scrollable: true, padding: 'none'})}>
            {children}
        </div>
    );
});

const ScrolledContentUl = React.forwardRef(function ScrolledContent(
    {children, ...props}: ComponentProps<'ul'>,
    ref: React.Ref<HTMLUListElement>
) {
    return (
        <ul {...props} ref={ref} className='box-content pb-4'>
            {children}
        </ul>
    );
});

const SearchHighlight = React.memo(function SearchHighlight({
    text,
    at: positions,
}: {
    text: string;
    at: PositionsMatcher;
}) {
    const [...nodes] = (function* () {
        let textNode = '';
        let boldNode = '';
        for (let i = 0; i < text.length; i++) {
            const char = text[i];
            if (positions.has(i)) {
                if (textNode) {
                    yield textNode;
                    textNode = '';
                }
                boldNode += char;
            } else {
                if (boldNode) {
                    yield <b key={i}>{boldNode}</b>;
                    boldNode = '';
                }
                textNode += char;
            }
        }
        if (boldNode) yield <b key={text.length}>{boldNode}</b>;
        if (textNode) yield textNode;
    })();

    return <Fragment>{nodes}</Fragment>;
});
